import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrgId } from '@/hooks/auth';
import { useDebounce } from '@/hooks/shared/useDebounce';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Link2, Briefcase, User, Building2, X } from 'lucide-react';
import type { EmailComposeLink } from '@/types/email';

interface LinkedEntity {
  type: 'deal' | 'lead' | 'company';
  id: string;
  name: string;
}

interface EmailLinkSearchFieldProps {
  links: EmailComposeLink[];
  onAddLink: (link: EmailComposeLink) => void;
  onRemoveLink: (index: number) => void;
}

export function EmailLinkSearchField({
  links,
  onAddLink,
  onRemoveLink,
}: EmailLinkSearchFieldProps) {
  const { orgId } = useCurrentOrgId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);

  const { data: results = [] } = useQuery({
    queryKey: ['email-link-search', orgId, debouncedQuery],
    queryFn: async (): Promise<LinkedEntity[]> => {
      if (!orgId || debouncedQuery.length < 2) return [];
      const pattern = `%${debouncedQuery}%`;
      const items: LinkedEntity[] = [];

      // Search deals
      const { data: deals } = await supabase
        .from('deals')
        .select('id, title')
        .eq('org_id', orgId)
        .ilike('title', pattern)
        .limit(5);
      if (deals) {
        for (const d of deals) {
          items.push({ type: 'deal', id: d.id, name: d.title || 'Sem título' });
        }
      }

      // Search leads
      const { data: leads } = await supabase
        .from('leads')
        .select('id, name')
        .eq('org_id', orgId)
        .ilike('name', pattern)
        .limit(5);
      if (leads) {
        for (const l of leads) {
          items.push({ type: 'lead', id: l.id, name: l.name || 'Sem nome' });
        }
      }

      // Search companies
      const { data: companies } = await supabase
        .from('companies')
        .select('id, name')
        .eq('org_id', orgId)
        .ilike('name', pattern)
        .limit(5);
      if (companies) {
        for (const c of companies) {
          items.push({ type: 'company', id: c.id, name: c.name || 'Sem nome' });
        }
      }

      return items;
    },
    enabled: !!orgId && debouncedQuery.length >= 2,
    staleTime: 15_000,
  });

  const handleSelect = (entity: LinkedEntity) => {
    const link: EmailComposeLink = { link_source: 'manual' };
    if (entity.type === 'deal') link.deal_id = entity.id;
    else if (entity.type === 'lead') link.lead_id = entity.id;
    else if (entity.type === 'company') link.company_id = entity.id;
    onAddLink(link);
    setOpen(false);
    setQuery('');
  };

  const entityIcon = (type: string) => {
    if (type === 'deal') return <Briefcase className="h-3.5 w-3.5 text-primary" />;
    if (type === 'lead') return <User className="h-3.5 w-3.5 text-blue-500" />;
    return <Building2 className="h-3.5 w-3.5 text-orange-500" />;
  };

  const entityLabel = (type: string) => {
    if (type === 'deal') return 'Deal';
    if (type === 'lead') return 'Lead';
    return 'Empresa';
  };

  // Group results
  const deals = results.filter((r) => r.type === 'deal');
  const leads = results.filter((r) => r.type === 'lead');
  const companies = results.filter((r) => r.type === 'company');

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Linked badges */}
        {links.map((link, i) => {
          const id = link.deal_id || link.lead_id || link.company_id || '';
          const type = link.deal_id ? 'deal' : link.lead_id ? 'lead' : 'company';
          return (
            <Badge key={`${type}-${id}-${i}`} variant="secondary" className="gap-1.5 text-xs">
              {entityIcon(type)}
              <span className="capitalize">{entityLabel(type)}</span>
              <button
                onClick={() => onRemoveLink(i)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-destructive/10 hover:text-destructive transition-colors"
                aria-label="Remover vínculo"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          );
        })}

        {/* Search trigger */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground hover:text-foreground gap-1.5"
            >
              <Link2 className="h-3 w-3" />
              {links.length === 0 ? 'Vincular a deal ou lead...' : 'Adicionar vínculo'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder="Buscar deal, lead ou empresa..."
              />
              <CommandList>
                {debouncedQuery.length < 2 ? (
                  <CommandEmpty>Digite pelo menos 2 caracteres...</CommandEmpty>
                ) : results.length === 0 ? (
                  <CommandEmpty>Nenhum resultado.</CommandEmpty>
                ) : (
                  <>
                    {deals.length > 0 && (
                      <CommandGroup heading="Deals">
                        {deals.map((d) => (
                          <CommandItem key={d.id} onSelect={() => handleSelect(d)} className="gap-2">
                            <Briefcase className="h-3.5 w-3.5 text-primary" />
                            <span className="truncate">{d.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    {leads.length > 0 && (
                      <CommandGroup heading="Leads">
                        {leads.map((l) => (
                          <CommandItem key={l.id} onSelect={() => handleSelect(l)} className="gap-2">
                            <User className="h-3.5 w-3.5 text-blue-500" />
                            <span className="truncate">{l.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    {companies.length > 0 && (
                      <CommandGroup heading="Empresas">
                        {companies.map((c) => (
                          <CommandItem key={c.id} onSelect={() => handleSelect(c)} className="gap-2">
                            <Building2 className="h-3.5 w-3.5 text-orange-500" />
                            <span className="truncate">{c.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
